// utils/firebaseFunctionCaller.js
/* eslint-disable max-len */

import { getApp } from "firebase/app";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";
import { auth } from "../firebaseConfig";
import { registrarFalhaRede } from "../features/relatoBug/buffers";

const DEFAULT_REGION = "southamerica-east1";

let emulatorBound = false;

/**
 * Chama uma Cloud Function "onCall" de forma segura.
 * - Usa o protocolo oficial do Firebase (httpsCallable).
 * - Evita serializar o objeto de erro (que causa o crash de cross-origin/toJSON).
 * - Em dev, você pode usar o emulador automaticamente.
 *
 * @param {string} submitBodyScan - Nome da função (ex.: "submitBodyScan").
 * @param {object} payload - Dados a enviar.
 * @param {object} [opts]
 * @param {string} [opts.region=DEFAULT_REGION] - Região do deploy.
 * @param {boolean} [opts.useEmulator=false] - Força uso do emulador (localhost:5001).
 * @returns {Promise<any>} - `data` retornado pela função.
 */

  function estimatePayloadBytesSafe(obj) {
    try {
      const seen = new WeakSet();
      const json = JSON.stringify(obj, (k, v) => {
        if (typeof v === "function") return undefined; 
        if (v && typeof v === "object") {
          if (seen.has(v)) return undefined;         
          seen.add(v);
        }
        return v;
      });
      return new Blob([json]).size;
    } catch (e) {
      console.warn("[callFirebaseFunction] Não foi possível estimar payload:", e?.message);
      return -1; 
    }
  }

export async function callFirebaseFunction(functionName, payload, opts = {}) {
  const region = opts.region || DEFAULT_REGION;
  const app = getApp();
  const functions = getFunctions(app, region);
  
  try {
    // @ts-ignore
    const pid = app?.options?.projectId || "(desconhecido)";
  } catch {}

  const SHOULD_USE_EMULATOR =
    opts.useEmulator === true ||
    (typeof process !== "undefined" &&
      process?.env?.REACT_APP_USE_FUNCTIONS_EMULATOR === "true");
  if (SHOULD_USE_EMULATOR && !emulatorBound) {
    try {
      connectFunctionsEmulator(functions, "localhost", 5001);
    } catch (_) {}
    emulatorBound = true;
  }

const user = auth.currentUser;
if (!user && functionName !== "checkCpfStatus" && functionName !== "startVerification" && functionName !== "verifyCode") {
  console.warn(`[callFirebaseFunction:${functionName}] Chamando sem usuário autenticado.`);
}

  const fn = httpsCallable(functions, functionName, { timeout: 300000 });

  try {
    const size = estimatePayloadBytesSafe(payload);
    if (size >= 0) { // só valida quando conseguimos medir
      if (size > 9.5 * 1024 * 1024) {
        console.error(`[callFirebaseFunction:${functionName}] payload ~${(size/1024/1024).toFixed(2)}MB — grande demais para callable.`);
        throw new Error("As fotos estão muito grandes para envio direto. Vamos enviar as imagens pelo Storage e mandar só os links.");
      } 
    }
    const res = await fn(payload);
    return res?.data;
  } catch (err) {
    const safe = {
      name: err?.name,
      code: err?.code,
      message: err?.message,
      details: err?.details,
    };
    console.error(`[callFirebaseFunction:${functionName}]`, safe);
    // Ponto único de falha de callable no app — o `code` do HttpsError já está
    // aqui, montado. No-op para quem não tem o claim do relato de bug.
    registrarFalhaRede({
      metodo: "CALLABLE",
      url: functionName,
      status: null,
      codigo: safe.code || null,
      requestId: safe.details?.requestId || null,
      detalhe: safe.message || null,
    });
    // Preserva `code` e `details` no erro propagado (aditivo: callers que leem
    // apenas `.message` seguem funcionando). Necessário para que o client
    // reconheça erros estruturados como QUOTA_EXCEEDED (ver utils/quotaError.js).
    const out = new Error(safe.message || "Falha ao chamar a função.");
    out.code = safe.code;
    out.details = safe.details;
    throw out;
  }
}

export default callFirebaseFunction;
